import { Link } from "react-router-dom";
import { ArrowRight, Truck, ShieldCheck, Utensils, Star } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion";
import PlanTabs from "../components/PlanTabs";
import MealCard from "../components/MealCard";
import WhyChoose from "../components/WhyChoose";
import HowItWorks from "../components/HowItWorks";
import {
  brand,
  heroImage,
  featuredMeals,
  familyBundles,
  testimonials,
  faqs,
  mealTags,
} from "../mock";
import PlanCard from "../components/PlanCard";

export default function Home() {
  return (
    <main>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-green-50 via-white to-amber-50" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid gap-10 lg:grid-cols-2 items-center">
          <div className="fade-in">
            <Badge className="bg-green-100 text-green-800 hover:bg-green-100 rounded-full px-3 py-1">
              Auckland&apos;s High-Protein Tiffin
            </Badge>
            <h1 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-neutral-900 leading-[1.05]">
              Fresh, healthy meals
              <span className="block text-green-700">delivered daily.</span>
            </h1>
            <p className="mt-5 text-lg text-neutral-600 max-w-xl">
              Nutritionist-designed Indian &amp; fusion tiffin plans — 100g+ protein, controlled oil, no preservatives. Weekly or monthly, no auto-renewal.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/plans">
                <Button className="bg-green-600 hover:bg-green-700 text-white rounded-full px-6 h-11">
                  Build Your Meal Plan <ArrowRight size={16} className="ml-1" />
                </Button>
              </Link>
              <Link to="/menu">
                <Button variant="outline" className="rounded-full px-6 h-11 border-neutral-300">
                  See Menu
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-5 text-sm text-neutral-600">
              <div className="flex items-center gap-2"><Truck size={16} className="text-green-700" /> Free delivery across Auckland</div>
              <div className="flex items-center gap-2"><ShieldCheck size={16} className="text-green-700" /> No preservatives</div>
              <div className="flex items-center gap-2"><Utensils size={16} className="text-green-700" /> No repeat meals in a week</div>
            </div>
          </div>
          <div className="relative fade-in">
            <div className="absolute -inset-4 bg-gradient-to-br from-green-200/50 to-amber-200/40 blur-2xl rounded-[40%]" />
            <img
              src={heroImage}
              alt="Healthy tiffin meal bowl"
              className="relative rounded-3xl shadow-2xl w-full aspect-[4/3] object-cover"
            />
            <div className="absolute -bottom-6 -left-4 md:-left-8 bg-white shadow-lg rounded-2xl p-4 border border-neutral-100 max-w-[220px]">
              <p className="text-xs text-neutral-500">Per daily box</p>
              <p className="text-lg font-extrabold text-neutral-900">100-120g protein</p>
              <p className="text-xs text-green-700 font-semibold">Covers 80-90% of daily needs</p>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="text-center">
          <p className="text-sm font-bold tracking-widest text-green-700">HOW IT WORKS</p>
          <h2 className="mt-2 text-3xl md:text-4xl font-extrabold text-neutral-900">Three easy steps to eat well</h2>
        </div>
        <HowItWorks />
      </section>

      {/* BUILD YOUR MEAL PLAN */}
      <section id="plans" className="bg-neutral-50 border-y border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-sm font-bold tracking-widest text-green-700">BUILD YOUR MEAL PLAN</p>
            <h2 className="mt-2 text-3xl md:text-4xl font-extrabold text-neutral-900">
              Pick from weekly or monthly plans
            </h2>
            <p className="mt-3 text-neutral-600">One-time payment only. No auto-renewal, no catch.</p>
          </div>
          <div className="mt-10">
            <PlanTabs />
          </div>
        </div>
      </section>

      {/* MEAL TAGS + FEATURED MEALS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="text-center">
          <p className="text-sm font-bold tracking-widest text-green-700">FRESH THIS WEEK</p>
          <h2 className="mt-2 text-3xl md:text-4xl font-extrabold text-neutral-900">Different meals every day</h2>
          <p className="mt-3 text-neutral-600 max-w-2xl mx-auto">
            Enjoy fresh, exciting healthy meals every day — we never repeat dishes in a week, so you get variety without the hassle.
          </p>
        </div>

        <div className="mt-8 flex flex-wrap gap-2 justify-center">
          {mealTags.map((t) => (
            <span
              key={t}
              className="tag-pill text-xs font-bold tracking-wider px-3 py-1.5 rounded-full border border-neutral-300 text-neutral-700 hover:border-green-600 hover:text-green-700 bg-white"
            >
              {t}
            </span>
          ))}
        </div>

        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {featuredMeals.map((m) => (
            <MealCard key={m.name} meal={m} />
          ))}
        </div>

        <div className="mt-10 text-center">
          <Link to="/menu">
            <Button variant="outline" className="rounded-full px-6 border-neutral-300">See full menu <ArrowRight size={16} className="ml-1" /></Button>
          </Link>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="bg-gradient-to-b from-white to-green-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="text-center">
            <p className="text-sm font-bold tracking-widest text-green-700">WHY {brand.name.toUpperCase()}</p>
            <h2 className="mt-2 text-3xl md:text-4xl font-extrabold text-neutral-900">Why choose our high-protein tiffin?</h2>
          </div>
          <WhyChoose />
        </div>
      </section>

      {/* FAMILY BUNDLES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="text-center max-w-3xl mx-auto">
          <p className="text-sm font-bold tracking-widest text-green-700">FAMILY BUNDLES</p>
          <h2 className="mt-2 text-3xl md:text-4xl font-extrabold text-neutral-900">Eat well together, save more</h2>
          <p className="mt-3 text-neutral-600">Minimum 2 people, same delivery address. Fresh meals for the whole family.</p>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {familyBundles.map((p) => (
            <PlanCard key={p.id} plan={p} />
          ))}
        </div>
      </section>

      {/* TRIAL CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-green-600 to-green-700" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-white grid gap-6 md:grid-cols-2 items-center">
          <div>
            <p className="text-sm font-bold tracking-widest text-green-100">STILL DECIDING?</p>
            <h2 className="mt-2 text-3xl md:text-4xl font-extrabold">Try the Full Day Plan for 5 days</h2>
            <p className="mt-3 text-green-50 max-w-xl">
              Same meals, same delivery, same experience as our weekly and monthly members — just without the commitment. No auto-renewal, no catch.
            </p>
          </div>
          <div className="md:justify-self-end">
            <Link to="/plans">
              <Button className="bg-white text-green-700 hover:bg-green-50 rounded-full h-12 px-7 font-bold">
                Start 5 Day Trial <ArrowRight size={16} className="ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="text-center">
          <p className="text-sm font-bold tracking-widest text-green-700">HAPPY CUSTOMERS</p>
          <h2 className="mt-2 text-3xl md:text-4xl font-extrabold text-neutral-900">Loved by Aucklanders</h2>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {testimonials.map((t) => (
            <div key={t.name} className="rounded-2xl border border-neutral-200 p-6 bg-white hover:shadow-md transition-shadow">
              <div className="flex gap-0.5 text-amber-500">
                {[...Array(t.rating)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
              </div>
              <p className="mt-4 text-neutral-700 leading-relaxed">“{t.text}”</p>
              <div className="mt-4 pt-4 border-t border-neutral-100">
                <p className="font-bold text-neutral-900">{t.name}</p>
                <p className="text-xs text-neutral-500">{t.location}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-neutral-50 border-t border-neutral-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="text-center">
            <p className="text-sm font-bold tracking-widest text-green-700">FAQ</p>
            <h2 className="mt-2 text-3xl md:text-4xl font-extrabold text-neutral-900">Frequently Asked Questions</h2>
          </div>
          <Accordion type="single" collapsible className="mt-8">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-neutral-200">
                <AccordionTrigger className="text-left font-semibold text-neutral-900 hover:text-green-700 hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-neutral-600 leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </main>
  );
}
